<script setup lang="ts">
import { onUnmounted, ref } from 'vue';
import type { ScheduledRetailClassDisplayAdInfo } from '@/types/classifiedOrder';
import { notifyModalClosed, notifyModalOpened } from '@/utils/iframe';
import ImageLightbox from './ImageLightbox.vue';
import ListingCreativePreview from './ListingCreativePreview.vue';

defineProps<{
  ad: ScheduledRetailClassDisplayAdInfo;
  categoryLabel?: string;
  batchStart?: boolean;
}>();

const showLightbox = ref(false);

function openLightbox() {
  notifyModalOpened();
  showLightbox.value = true;
}

function closeLightbox() {
  if (!showLightbox.value) return;
  notifyModalClosed();
  showLightbox.value = false;
}

function handleImageError(event: Event) {
  (event.target as HTMLImageElement).style.display = 'none';
}

onUnmounted(() => {
  if (showLightbox.value) {
    notifyModalClosed();
  }
});
</script>

<template>
  <div
    class="display-listing-card"
    :class="{ 'category-batch-start': batchStart }"
  >
    <ListingCreativePreview
      :image-url="ad.adImageURL"
      :image-alt="ad.adImageFilename || 'Classified display listing'"
      :category-label="categoryLabel"
      loading="lazy"
      @activate="openLightbox"
      @error="handleImageError"
    />
  </div>
  
  <ImageLightbox
    :images="[ad.adImageURL]"
    :is-open="showLightbox"
    @close="closeLightbox"
  />
</template>

<style scoped>
.display-listing-card {
  position: relative;
  background: var(--bg-secondary);
  border: 2px solid var(--accent-primary);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-md);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.display-listing-card.category-batch-start {
  margin-top: 10px;
}
</style>
